import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertService } from '../../../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../../core/services/toast/toast.service';
import { cloneDeep } from 'lodash-es';
import { AffiliatePayment } from '../../../../../../../models/interfaces/affiliate_payment.interface';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-modal-edit-affiliate-payment',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './modal-edit-affiliate-payment.component.html',
    styleUrl: './modal-edit-affiliate-payment.component.scss'
})
export class ModalEditAffiliatePaymentComponent implements OnInit, OnDestroy {
  editMode = false;
  showCloseAlert = false;
  isSavingPayment = false;
  paymentForm: FormGroup = new FormGroup({});

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalEditAffiliatePaymentComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const { affiliateUserId } = receivedData;
    if (!affiliateUserId) return this.closeModal();

    const receivedPayment: AffiliatePayment = receivedData?.payment;
    if (receivedPayment) {
      this.editMode = true;
    }

    this.paymentForm = new FormGroup({
      affiliatePaymentId: new FormControl(receivedPayment?.affiliatePaymentId || uuidv4(), Validators.required),
      name: new FormControl(receivedPayment?.name || null, Validators.required),
      startDate: new FormControl(
        receivedPayment?.startDate ? DateTime.fromISO(receivedPayment.startDate).toFormat('yyyy-LL-dd') : null,
        Validators.required
      ),
      endDate: new FormControl(
        receivedPayment?.endDate ? DateTime.fromISO(receivedPayment.endDate).toFormat('yyyy-LL-dd') : null,
        Validators.required
      ),
      totalToPay: new FormControl(receivedPayment?.totalToPay || null, [Validators.required, Validators.min(0)]),
      affiliateUserId: new FormControl(affiliateUserId || null, Validators.required)
    });
    this.listenForChanges();
  }

  listenForChanges(): void {
    this.paymentForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
    });
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  async saveAffiliatePayment(): Promise<void> {
    try {
      this.isSavingPayment = true;
      const formValue = this.paymentForm.value;
      const startDate = DateTime.fromFormat(formValue.startDate, 'yyyy-LL-dd').startOf('day').toUTC().toISO();
      const endDate = DateTime.fromFormat(formValue.endDate, 'yyyy-LL-dd').endOf('day').toUTC().toISO();
      const body = { ...this.paymentForm.value };
      body.startDate = startDate;
      body.endDate = endDate;
      const response = await firstValueFrom(this.api.saveAffiliatePayment(body));
      this.isSavingPayment = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ paymentUpdated: response.paymentUpdated });
    } catch (errorResponse: any) {
      this.isSavingPayment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando pago de afiliado');
    }
  }
}
